import { notification } from 'antd';
import { get } from 'lodash-es';
import { useEffect } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import { RouteEnum } from '@enum/route.enum';
import { useCurrentApplication } from '@hook/application/use-current-application.hook';
import { usePrevious } from '@hook/common/use-previous.hook';
import { useLazyLoadLimitConsumersQuery } from '@hook/consumer/consumer.hooks';
import { useStoreSelector } from '@hook/store/store.hooks';
import { PaginationCursorResponseInterface } from '@interface/logs/response-data.interface';

const PAGINATION_PER_PAGE = 10;

export const useConsumers = () => {
  const navigate = useNavigate();
  const [api, contextHolder] = notification.useNotification();
  const isCredentialsExist = useStoreSelector((state) => state.credentials.data.length > 0);
  const count = useStoreSelector((state) => Object.keys(state.consumers.data).length);
  const prevCount = usePrevious(count);
  const [action, { isLoading, data }] = useLazyLoadLimitConsumersQuery();
  const { hasToken } = useCurrentApplication();

  const onSelectConsumer = (consumerId: string) => () => {
    if (!isCredentialsExist) {
      return api.warning({
        message: 'Consumer need credentials',
        description:
          'Before accessing the consumer page, you need to create credentials. You can do this in the "Credentials" table by clicking on the "Add credentials" button',
      });
    }

    navigate(generatePath(RouteEnum.ConsumerView.path, { consumerId }));
  };

  const onNext = () => void action({ pagination: { limit: PAGINATION_PER_PAGE, cursor: get(data, 'meta.next') } });

  const onPrevious = () =>
    void action({ pagination: { limit: PAGINATION_PER_PAGE, cursor: get(data, 'meta.previous') } });

  useEffect(
    () =>
      void (
        isCredentialsExist &&
        hasToken &&
        count !== prevCount &&
        action({ pagination: { limit: PAGINATION_PER_PAGE } }).unwrap()
      ),
    [isCredentialsExist, hasToken, count, prevCount],
  );

  return {
    data: get(data, 'data', []),
    isLoading,
    pagination: get(data, 'meta') as PaginationCursorResponseInterface,
    onNext,
    onPrevious,
    onSelectConsumer,
    contextHolder,
  };
};
